/**
 * Connector for the Partner V1 Portal to handle token retrieval.
 *
 * Key Points:
 * - Uses the legacy `/api/user/AuthContext` endpoint specific to Partner Portal.
 * - Maintains backward compatibility with unique token mappings (TokenTypeConversion).
 * - Ensures separation of concerns by isolating logic specific to Partner V1 Portal.
 */
import { httpRequestRetryStore } from '../../services/http-request-store';
import { HttpFunctions } from '../../services/http-service';
import { trackError } from '../../services/telemetry-service/library-telemetry-service';
import { ErrorInformation } from '../../services/telemetry-service/types/error-information';
import { ConnectorPromiseStore } from '../../services/types/window-connectors';
import { HarmonyConnectorError } from '../connector-error';
import { localeConnector } from '../locale-connector/locale-connector';
import { convertTokenTypeForPartner, deriveTokenFromAuthContextForPartner } from '../../helpers/auth-context-partner-utils';
import { BaseAuthContextApi } from '../../types/auth-context-types';
import { AuthContext, AuthContextType } from './auth-context-connector';

let authContextData: AuthContextPartnerV1PortalApi | undefined;
const authContextUrl = '/api/user/AuthContext'; // maintaining v1 using authcontext API
const ErrorSource = 'AuthContextConnectorPartnerV1Portal';
const FailureAuthContext = 'Failure while trying to retrieve auth Context';

// This is the unique config Id for auth context connector. You need to change this if there is a change in design.
const CONFIG_ID = 'V1';

type AuthContextPartnerV1PortalApi = BaseAuthContextApi; // Alias for clarity

type TokenTypePartnerV1Portal = 'msGraphToken' | 'partnerCenterAppToken' | 'partnerApiToken' | 'armToken' | 'copilotToken';

const TokenTypeConversion = new Map<AuthContextType, TokenTypePartnerV1Portal>([
  [AuthContextType.MSGraph, 'msGraphToken'],
  [AuthContextType.PartnerCenterApp, 'partnerCenterAppToken'],
  [AuthContextType.PartnerApi, 'partnerApiToken'],
  [AuthContextType.ARM, 'armToken'],
  [AuthContextType.CopilotToken, 'copilotToken'],
]);

/**
 * Auth Context Connector for Partner Portal. This handles the behavior on how to get token on partner portal side. This is using
 * Classic Front Door Setup.
 * @param authContextType Auth context types as requested by workspace.
 * @returns The token.
 */
export const authContextConnectorPartnerV1Portal = async (authContextType: AuthContextType[], forceNew: boolean) => {
  const authContext: AuthContext = {};
  if (authContextData && !forceNew) {
    deriveTokenFromAuthContextWrapper(authContextType, authContext);
    if (authContext) return authContext;
  }

  const locale = localeConnector();
  const baseUrl = `${window.location.origin}/${locale}`;
  const url = `${baseUrl}${authContextUrl}`;
  const connectorPromiseStoreConfig: ConnectorPromiseStore = {
    connectorType: 'authContext',
    uniqueId: url + CONFIG_ID,
  };
  try {
    const authContextResponse = await httpRequestRetryStore<AuthContextPartnerV1PortalApi>(connectorPromiseStoreConfig, url, HttpFunctions.GET, null, null, 'same-origin', forceNew);
    if (authContextResponse) {
      // Update the global authContextData with the API response for reuse in subsequent calls
      authContextData = authContextResponse;
      deriveTokenFromAuthContextWrapper(authContextType, authContext);
    } else {
      throw new HarmonyConnectorError(ErrorSource, 'AuthContextResponse is null or undefined');
    }
  } catch (error: any) {
    const message: string = error?.message ?? FailureAuthContext;
    const statusCode: string = error?.statusCode ?? '500';

    const errorInformation: ErrorInformation = {
      errorMessage: message,
      source: ErrorSource,
      statusCode: statusCode,
      errorObject: JSON.stringify(error) ?? '',
    };
    trackError('auth-context-failure', errorInformation);
    throw new HarmonyConnectorError(ErrorSource, FailureAuthContext);
  }

  return authContext;
};

const deriveTokenFromAuthContextWrapper = (authContextType: AuthContextType[], authContext: AuthContext): void => {
  deriveTokenFromAuthContextForPartner(authContextType, authContext, authContextData as Record<string, any>, TokenTypeConversion);
};
